// /frontend/src/pages/auth/RegisterPage.jsx (Complete version)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, clearError } from '../../store/slices/authSlice';
import Layout from '../../components/layout/Layout';

const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Indonesia'
    },
    questionnaire: {
      designStyle: 'modern',
      roomTypes: [],
      budget: 'medium',
      homeType: 'apartment',
      colorPreferences: [],
      specificRequirements: ''
    }
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect path from URL query or default to home page
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') || '/';
  
  const { loading, isAuthenticated, error } = useSelector((state) => state.auth);
  
  // Check for duplicate email error from Redux state
  useEffect(() => {
    if (error && error.includes('Email address is already registered')) {
      setEmailAlreadyExists(true);
      setFormErrors({
        ...formErrors,
        email: 'Email address is already registered'
      });
    }
  }, [error]);
  
  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect);
    }
    
    // Clear any previous auth errors when component mounts
    dispatch(clearError());
  }, [isAuthenticated, navigate, redirect, dispatch]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear email already exists error when user types in email field
    if (name === 'email' && emailAlreadyExists) {
      setEmailAlreadyExists(false);
      setFormErrors({
        ...formErrors,
        email: ''
      });
      dispatch(clearError());
    }
    
    // Handle nested objects (address and questionnaire)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Handle roomTypes checkbox array
    if (name === 'roomTypes') {
      const updatedRoomTypes = [...formData.questionnaire.roomTypes];
      
      if (checked) {
        // Add to array if checked
        updatedRoomTypes.push(value);
      } else {
        // Remove from array if unchecked
        const index = updatedRoomTypes.indexOf(value);
        if (index > -1) {
          updatedRoomTypes.splice(index, 1);
        }
      }
      
      setFormData({
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          roomTypes: updatedRoomTypes
        }
      });
    }
    
    // Handle colorPreferences checkbox array
    if (name === 'colorPreferences') {
      const updatedColorPreferences = [...formData.questionnaire.colorPreferences];
      
      if (checked) {
        // Add to array if checked
        updatedColorPreferences.push(value);
      } else {
        // Remove from array if unchecked
        const index = updatedColorPreferences.indexOf(value);
        if (index > -1) {
          updatedColorPreferences.splice(index, 1);
        }
      }
      
      setFormData({
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          colorPreferences: updatedColorPreferences
        }
      });
    }
  };
  
  const validateStep1 = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (formData.phone && !/^(\+\d{1,3}[- ]?)?\d{10,15}$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateStep2 = () => {
    const errors = {};
    
    if (!formData.address.street.trim()) {
      errors['address.street'] = 'Street address is required';
    }
    
    if (!formData.address.city.trim()) {
      errors['address.city'] = 'City is required';
    }
    
    if (!formData.address.state.trim()) {
      errors['address.state'] = 'State/Province is required';
    }
    
    if (!formData.address.zipCode.trim()) {
      errors['address.zipCode'] = 'ZIP/Postal Code is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleNextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };
  
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Final submission - no validation for questionnaire, it's optional
    dispatch(register({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      address: formData.address,
      questionnaire: formData.questionnaire
    }));
  };
  
  // Render step 1 - Basic Info
  const renderStep1 = () => (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 1: Account Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              formErrors.firstName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="First name"
          />
          {formErrors.firstName && (
            <p className="text-red-600 text-xs mt-1">{formErrors.firstName}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              formErrors.lastName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Last name"
          />
          {formErrors.lastName && (
            <p className="text-red-600 text-xs mt-1">{formErrors.lastName}</p>
          )}
        </div>
      </div>
      
      <div className="mb-4">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            formErrors.email ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Enter your email"
        />
        {formErrors.email && (
          <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>
        )}
        {emailAlreadyExists && (
          <div className="flex items-center mt-2">
            <p className="text-xs text-gray-600">
              Already have an account?{' '}
              <Link 
                to={`/login?email=${encodeURIComponent(formData.email)}`} 
                className="text-teal-800 hover:text-teal-900 font-medium"
              >
                Sign in here
              </Link>
            </p>
          </div>
        )}
      </div>
      
      <div className="mb-4">
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number (optional)
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            formErrors.phone ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Enter your phone number"
        />
        {formErrors.phone && (
          <p className="text-red-600 text-xs mt-1">{formErrors.phone}</p>
        )}
      </div>
      
      <div className="mb-4">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            formErrors.password ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Create a password"
        />
        {formErrors.password && (
          <p className="text-red-600 text-xs mt-1">{formErrors.password}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Password must be at least 6 characters
        </p>
      </div>
      
      <div className="mb-6">
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Confirm Password
        </label>
        <input
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            formErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Confirm your password"
        />
        {formErrors.confirmPassword && (
          <p className="text-red-600 text-xs mt-1">{formErrors.confirmPassword}</p>
        )}
      </div>
      
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleNextStep}
          className="py-2 px-4 rounded-md font-medium text-white bg-teal-800 hover:bg-teal-900 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
  
  // Render step 2 - Address Information
  const renderStep2 = () => (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 2: Address Information</h2>
      
      <div className="mb-4">
        <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
          Street Address
        </label>
        <input
          type="text"
          id="street"
          name="address.street"
          value={formData.address.street}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            formErrors['address.street'] ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Enter your street address"
        />
        {formErrors['address.street'] && (
          <p className="text-red-600 text-xs mt-1">{formErrors['address.street']}</p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <input
            type="text"
            id="city"
            name="address.city"
            value={formData.address.city}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              formErrors['address.city'] ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="City"
          />
          {formErrors['address.city'] && (
            <p className="text-red-600 text-xs mt-1">{formErrors['address.city']}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
            State/Province
          </label>
          <input
            type="text"
            id="state"
            name="address.state"
            value={formData.address.state}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              formErrors['address.state'] ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="State/Province"
          />
          {formErrors['address.state'] && (
            <p className="text-red-600 text-xs mt-1">{formErrors['address.state']}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
            ZIP/Postal Code
          </label>
          <input
            type="text"
            id="zipCode"
            name="address.zipCode"
            value={formData.address.zipCode}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              formErrors['address.zipCode'] ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="ZIP/Postal Code"
          />
          {formErrors['address.zipCode'] && (
            <p className="text-red-600 text-xs mt-1">{formErrors['address.zipCode']}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <select
            id="country"
            name="address.country"
            value={formData.address.country}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="Indonesia">Indonesia</option>
            <option value="Malaysia">Malaysia</option>
            <option value="Singapore">Singapore</option>
            <option value="Thailand">Thailand</option>
            <option value="Vietnam">Vietnam</option>
            <option value="Philippines">Philippines</option>
          </select>
        </div>
      </div>
      
      <div className="flex justify-between">
        <button
          type="button"
          onClick={handlePrevStep}
          className="py-2 px-4 rounded-md font-medium text-teal-800 border border-teal-800 hover:bg-teal-50 transition-colors"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={handleNextStep}
          className="py-2 px-4 rounded-md font-medium text-white bg-teal-800 hover:bg-teal-900 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
  
  // Render step 3 - Questionnaire
  const renderStep3 = () => (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 3: Design Preferences (Optional)</h2>
      <p className="text-sm text-gray-600 mb-6">
        Help us understand your design preferences to provide better recommendations.
      </p>
      
      <div className="mb-4">
        <label htmlFor="designStyle" className="block text-sm font-medium text-gray-700 mb-1">
          What design style do you prefer?
        </label>
        <select
          id="designStyle"
          name="questionnaire.designStyle"
          value={formData.questionnaire.designStyle}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="modern">Modern</option>
          <option value="minimalist">Minimalist</option>
          <option value="traditional">Traditional</option>
          <option value="industrial">Industrial</option>
          <option value="scandinavian">Scandinavian</option>
          <option value="bohemian">Bohemian</option>
          <option value="other">Other</option>
        </select>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Which rooms are you looking to furnish? (Select all that apply)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Office', 'Dining Room', 'Outdoor'].map((room) => {
            const value = room.toLowerCase().replace(' room', '');
            return (
              <div key={value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`room-${value}`}
                  name="roomTypes"
                  value={value}
                  checked={formData.questionnaire.roomTypes.includes(value)}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <label htmlFor={`room-${value}`} className="ml-2 text-sm text-gray-700">
                  {room}
                </label>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mb-4">
        <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
          What is your budget range?
        </label>
        <select
          id="budget"
          name="questionnaire.budget"
          value={formData.questionnaire.budget}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="low">Budget-Friendly (Under $1,000)</option>
          <option value="medium">Mid-Range ($1,000 - $3,000)</option>
          <option value="high">High-End ($3,000 - $10,000)</option>
          <option value="luxury">Luxury (Over $10,000)</option>
        </select>
      </div>
      
      <div className="mb-4">
        <label htmlFor="homeType" className="block text-sm font-medium text-gray-700 mb-1">
          What type of home do you have?
        </label>
        <select
          id="homeType"
          name="questionnaire.homeType"
          value={formData.questionnaire.homeType}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="apartment">Apartment</option>
          <option value="house">House</option>
          <option value="condo">Condominium</option>
          <option value="studio">Studio</option>
          <option value="villa">Villa</option>
          <option value="other">Other</option>
        </select>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          What colors do you prefer in your space? (Select all that apply)
        </label>
        <div className="grid grid-cols-3 gap-2">
          {['Neutral', 'White', 'Black', 'Gray', 'Blue', 'Green', 'Yellow', 'Red', 'Brown', 'Purple', 'Pink', 'Orange'].map((color) => {
            const value = color.toLowerCase();
            return (
              <div key={value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`color-${value}`}
                  name="colorPreferences"
                  value={value}
                  checked={formData.questionnaire.colorPreferences.includes(value)}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <label htmlFor={`color-${value}`} className="ml-2 text-sm text-gray-700">
                  {color}
                </label>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mb-6">
        <label htmlFor="specificRequirements" className="block text-sm font-medium text-gray-700 mb-1">
          Any specific requirements or preferences?
        </label>
        <textarea
          id="specificRequirements"
          name="questionnaire.specificRequirements"
          value={formData.questionnaire.specificRequirements}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          rows="3"
          placeholder="Tell us about any special requirements or preferences you have for your furniture and decor..."
        ></textarea>
      </div>
      
      <div className="flex justify-between">
        <button
          type="button"
          onClick={handlePrevStep}
          className="py-2 px-4 rounded-md font-medium text-teal-800 border border-teal-800 hover:bg-teal-50 transition-colors"
        >
          Previous
        </button>
        <button
          type="submit"
          disabled={loading || emailAlreadyExists}
          className={`py-2 px-4 rounded-md font-medium text-white ${
            loading
              ? 'bg-teal-600 cursor-wait'
              : emailAlreadyExists
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-teal-800 hover:bg-teal-900'
          } transition-colors`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Creating Account...
            </div>
          ) : (
            'Create Account'
          )}
        </button>
      </div>
    </div>
  );
  
  // Registration progress indicator
  const renderProgress = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            currentStep >= 1 ? 'bg-teal-800 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            1
          </div>
          <span className="text-xs mt-1">Account</span>
        </div>
        <div className={`flex-1 h-1 mx-2 ${currentStep >= 2 ? 'bg-teal-800' : 'bg-gray-200'}`}></div>
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            currentStep >= 2 ? 'bg-teal-800 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            2
          </div>
          <span className="text-xs mt-1">Address</span>
        </div>
        <div className={`flex-1 h-1 mx-2 ${currentStep >= 3 ? 'bg-teal-800' : 'bg-gray-200'}`}></div>
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            currentStep >= 3 ? 'bg-teal-800 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            3
          </div>
          <span className="text-xs mt-1">Preferences</span>
        </div>
      </div>
    </div>
  );
  
  return (
    <Layout>
      <div className="max-w-2xl mx-auto my-16 px-4 sm:px-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">Create Account</h1>
          
          {/* Progress indicator */}
          {renderProgress()}
          
          {/* Error alert */}
          {error && !error.includes('Email address is already registered') && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to={redirect ? `/login?redirect=${redirect}` : '/login'} 
                className="text-teal-800 hover:text-teal-900 font-medium"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterPage;